import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, PLATFORM_ADMIN_WORKSPACE_SLUG, TenantWorkspace } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type Flag = { key: string; enabled: boolean; description: string; updatedAt: string };
type WorkspaceSnapshot = {
  workspace: TenantWorkspace;
  planName: string | null;
  capabilities: Record<string, boolean>;
  waived: boolean;
};

export default function AdminFeatureFlags() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [snapshots, setSnapshots] = useState<WorkspaceSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    Promise.all([
      api.featureFlags(),
      api.workspace().catch(() => ({ workspaces: [] as TenantWorkspace[] }))
    ])
      .then(async ([flagData, workspaceData]) => {
        setFlags(flagData.flags);
        const workspaces = workspaceData.workspaces ?? [];
        const details = await Promise.all(
          workspaces.map((w) =>
            api.workspacePlanDetail(w.id)
              .then((d) => ({
                workspace: w,
                planName: d.package?.name ?? d.subscription?.planId ?? null,
                capabilities: (d.capabilities ?? {}) as Record<string, boolean>,
                waived: Boolean(d.package?.waived)
              }))
              .catch(() => ({ workspace: w, planName: null, capabilities: {}, waived: false }))
          )
        );
        setSnapshots(details);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const capabilityKeys = Array.from(
    new Set(snapshots.flatMap((s) => Object.keys(s.capabilities)))
  ).sort();

  // B7 — the platform-admin workspace is always present (it's how the
  // operator signs in) but it's not a "tenant override" in any
  // meaningful sense — surfacing it inflates the badge count and makes
  // single-tenant installs look like they have an override they don't.
  const tenantSnapshots = snapshots.filter(
    (s) => s.workspace.slug !== PLATFORM_ADMIN_WORKSPACE_SLUG
  );

  const tFlags = useTableState(flags as unknown as Array<Record<string, unknown>>, {
    searchFields: ["key", "description"],
    initialPageSize: 25
  });
  const tenantSnapshotRows = tenantSnapshots.map((s) => ({
    snapshot: s,
    workspaceName: (s.workspace as { workspaceName?: string }).workspaceName ?? s.workspace.name ?? s.workspace.id,
    workspaceSlug: s.workspace.slug,
    workspaceId: s.workspace.id,
    planName: s.planName ?? ""
  }));
  type TenantOverrideRow = (typeof tenantSnapshotRows)[number];
  const tOverrides = useTableState(tenantSnapshotRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["workspaceName", "workspaceSlug", "workspaceId", "planName"],
    initialPageSize: 25
  });

  const toggle = async (f: Flag) => {
    setBusy(f.key);
    setErr(null);
    try {
      const { flag } = await api.updateFeatureFlag(f.key, { enabled: !f.enabled });
      setFlags((prev) => prev.map((p) => (p.key === flag.key ? flag : p)));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Feature flags</h1>
          <p>Platform-wide feature toggles. Changes take effect immediately for new requests.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <>
        {flags.length === 0 ? (
          <EmptyState
            icon="🚩"
            title="No feature flags registered"
            description="Flags appear here once a deploy registers them via the platform API. Check the deployed API version if you expect flags here."
          />
        ) : (
        <div>
          <TableToolbar
            search={tFlags.search} setSearch={tFlags.setSearch}
            page={tFlags.page} setPage={tFlags.setPage}
            pageSize={tFlags.pageSize} setPageSize={tFlags.setPageSize}
            totalPages={tFlags.totalPages} totalRows={tFlags.totalRows}
            hideExport
            searchPlaceholder="Search flag key / description…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Flag</th><th>State</th><th>Description</th><th>Updated</th><th></th></tr>
            </thead>
            <tbody>
              {(tFlags.paged as unknown as Flag[]).map((f) => (
                <tr key={f.key}>
                  <td><code>{f.key}</code></td>
                  <td>
                    <span className={`pill ${f.enabled ? "pill-success" : "pill-muted"}`}>
                      {f.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="soft">{f.description}</td>
                  <td className="soft">{new Date(f.updatedAt).toLocaleString()}</td>
                  <td>
                    <button className="btn btn-small" type="button" disabled={busy === f.key} onClick={() => toggle(f)}>
                      {busy === f.key ? "Saving…" : f.enabled ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
       )}

        {tenantSnapshots.length > 0 && capabilityKeys.length > 0 && (
          <section className="section">
            <div className="section-head">
              <h2>Per-tenant capability overrides ({tenantSnapshots.length})</h2>
            </div>
            <TableToolbar
              search={tOverrides.search} setSearch={tOverrides.setSearch}
              page={tOverrides.page} setPage={tOverrides.setPage}
              pageSize={tOverrides.pageSize} setPageSize={tOverrides.setPageSize}
              totalPages={tOverrides.totalPages} totalRows={tOverrides.totalRows}
              hideExport
              searchPlaceholder="Search workspace / plan…"
            />
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Plan</th>
                    {capabilityKeys.map((k) => <th key={k} style={{ fontSize: 11 }}>{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(tOverrides.paged as unknown as TenantOverrideRow[]).map((row) => {
                    const s = row.snapshot;
                    return (
                      <tr key={s.workspace.id}>
                        <td>
                          <Link to={`/admin/workspaces/${encodeURIComponent(s.workspace.id)}`}>
                            {(s.workspace as { workspaceName?: string }).workspaceName ?? s.workspace.name ?? s.workspace.id}
                          </Link>
                          {s.waived && <span className="pill pill-warning" style={{ marginLeft: 6 }}>waived</span>}
                        </td>
                        <td className="soft">{s.planName ?? "—"}</td>
                        {capabilityKeys.map((k) => (
                          <td key={k} style={{ textAlign: "center" }}>
                            {s.capabilities[k] === true ? "✓" : s.capabilities[k] === false ? "—" : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
        </>
      )}
    </div>
  );
}
